import {createSlice, createAsyncThunk} from "@reduxjs/toolkit";
import {GetInstallator} from "../../interfaces/User";
import apiV2 from "../../service/api/ApiV2";

interface InstallatorsState {
  data: GetInstallator[];
  loading: boolean;
  error: string | null;
}

const initialState: InstallatorsState = {
  data: [],
  loading: false,
  error: null,
};

export const fetchInstallators = createAsyncThunk(
  "installators/fetchInstallators",
  async (agencyIds?: number[] | null) => {
    if (!agencyIds || agencyIds.length === 0) {
      return apiV2.installators();
    }
    if (agencyIds.length === 1) {
      return apiV2.installators(agencyIds[0]);
    }
    // `/user/installators` ne filtre que sur un parc : l'union des appels donne les installateurs
    // partageant au moins un parc avec l'installation, même règle que le client (APP-4628).
    // Un appel par parc sélectionné, donc, et un installateur présent dans plusieurs parcs revient
    // dans plusieurs réponses — d'où le dédoublonnage. APP-4642 demande un endpoint acceptant
    // plusieurs parcs, qui ramènerait le tout à un seul appel.
    const responses = await Promise.all(agencyIds.map((agencyId) => apiV2.installators(agencyId)));
    const byId = new Map<number, GetInstallator>();
    for (const installator of responses.flat()) {
      byId.set(installator.id, installator);
    }
    return Array.from(byId.values()).sort((a, b) =>
      `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`),
    );
  },
);

const installatorsSlice = createSlice({
  name: "installators",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchInstallators.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInstallators.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchInstallators.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "An error occurred";
      });
  },
});

export default installatorsSlice.reducer;
